import React, { useState } from 'react';
import { 
  MoreVertical, 
  Eye, 
  CheckCircle,
  XCircle,
  Star,
  MessageSquare,
  User,
  Calendar,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw
} from 'lucide-react';
import useAdminStore from '../../../store/adminStore';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const ReviewTable = ({ 
  reviews, 
  pagination,
  onView, 
  onRefresh,
  onPageChange,
  isLoading = false 
}) => {
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [showModerateModal, setShowModerateModal] = useState(false);
  const [moderationNotes, setModerationNotes] = useState('');
  const [moderateAction, setModerateAction] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(pagination?.limit || 20);
  
  const { moderateReview } = useAdminStore();

  const handleModerate = async () => {
    try {
      await moderateReview(selectedReviewId, moderateAction, moderationNotes);
      setShowModerateModal(false);
      setModerationNotes('');
      setModerateAction(null);
      setSelectedReviewId(null);
      setShowActions(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Moderate review error:', error);
    }
  };

  const handlePageChange = (newPage) => {
    if (onPageChange && newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      onPageChange(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    if (onPageChange) {
      onPageChange(1, newLimit);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      APPROVED: { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
      PENDING: { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: AlertTriangle },
      REJECTED: { color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
    };
    
    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${config.color} shadow-neomorph-inset`}>
        <Icon className="h-2.5 w-2.5 mr-0.5" />
        {status}
      </span>
    );
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-2.5 w-2.5 ${
              i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-0.5 text-[10px] font-medium text-gray-700">{rating}</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'N/A';
    }
  };

  const total = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;
  const limit = pagination?.limit || 20;
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  return (
    <div className="bg-white rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-xs">
          <thead className="bg-[#E8ECF5]">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Review</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Reviewer</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Business</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Rating</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Date</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="7" className="px-3 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <RefreshCw className="h-4 w-4 text-primary-500 animate-spin" />
                    <span className="text-xs text-gray-500">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : reviews.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-3 py-8 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 shadow-neomorph-inset mb-3">
                    <MessageSquare className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 font-medium">No reviews found</p>
                </td>
              </tr>
            ) : (
              reviews.map((review) => (
                <tr key={review.id} className="hover:bg-[#E8ECF5] transition-colors">
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-gray-800 max-w-xs truncate">
                      {review.title || 'No title'}
                    </div>
                    <div className="text-[10px] text-gray-500 max-w-xs truncate mt-0.5 bg-[#E8ECF5] p-1 rounded shadow-neomorph-inset">
                      {review.comment || 'No comment'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="h-6 w-6 flex-shrink-0">
                        {review.reviewer?.avatar ? (
                          <img 
                            src={review.reviewer.avatar} 
                            alt={review.reviewer.firstName}
                            className="h-6 w-6 rounded-lg object-cover shadow-neomorph-inset"
                          />
                        ) : (
                          <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center">
                            <User className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="ml-2">
                        <div className="text-xs font-medium text-gray-800">
                          {review.reviewer?.firstName} {review.reviewer?.lastName}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {review.reviewer?.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-gray-800">
                      {review.business?.name}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {review.product ? `Product: ${review.product.name}` : 
                       review.service ? `Service: ${review.service.title}` : 
                       'Business Review'}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {renderStars(review.rating)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getStatusBadge(review.status)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500">
                    <span className="bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset">
                      {formatDate(review.createdAt)}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap relative">
                    <button
                      onClick={() => {
                        setSelectedReviewId(review.id);
                        setShowActions(!showActions);
                      }}
                      className="p-1 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-600"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </button>
                    
                    {selectedReviewId === review.id && showActions && (
                      <div className="absolute right-0 mt-1 w-44 bg-white rounded-xl shadow-neomorph border border-gray-100 z-10 overflow-hidden">
                        <button
                          onClick={() => {
                            onView(review);
                            setShowActions(false);
                            setSelectedReviewId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-[#E8ECF5] transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5 mr-2 text-primary-600" />
                          View
                        </button>
                        
                        {review.status !== 'APPROVED' && (
                          <button
                            onClick={() => {
                              setSelectedReviewId(review.id);
                              setModerateAction('APPROVED');
                              setShowModerateModal(true);
                              setShowActions(false);
                            }}
                            className="flex items-center w-full px-3 py-2 text-xs text-green-600 hover:bg-green-50 transition-colors"
                          >
                            <CheckCircle className="h-3.5 w-3.5 mr-2" />
                            Approve
                          </button>
                        )}
                        
                        {review.status !== 'REJECTED' && (
                          <button
                            onClick={() => {
                              setSelectedReviewId(review.id);
                              setModerateAction('REJECTED');
                              setShowModerateModal(true);
                              setShowActions(false);
                            }}
                            className="flex items-center w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <XCircle className="h-3.5 w-3.5 mr-2" />
                            Reject
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {!isLoading && reviews.length > 0 && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center space-x-1.5">
              <label className="text-[10px] text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-1.5 py-0.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-[10px]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-[10px] text-gray-500">per page</span>
            </div>

            <div className="text-[10px] text-gray-600">
              Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
              <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
              <span className="font-medium text-gray-800">{total}</span> reviews
            </div>

            <div className="flex items-center space-x-0.5">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="First"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-0.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-6 h-6 rounded-lg text-[10px] font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Last"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Moderate Modal */}
      {showModerateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowModerateModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-4 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl sm:mx-0 shadow-lg ${
                  moderateAction === 'APPROVED' 
                    ? 'bg-gradient-to-br from-green-500 to-green-600 shadow-green-500/30' 
                    : 'bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30'
                }`}>
                  {moderateAction === 'APPROVED' ? (
                    <CheckCircle className="h-6 w-6 text-white" />
                  ) : (
                    <XCircle className="h-6 w-6 text-white" />
                  )}
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <div className="flex items-center mb-1">
                    <div className={`w-1 h-4 bg-gradient-to-b rounded-full mr-2 ${
                      moderateAction === 'APPROVED' 
                        ? 'from-green-400 to-green-600' 
                        : 'from-red-400 to-red-600'
                    }`} />
                    <h3 className="text-base font-semibold text-gray-800">
                      {moderateAction === 'APPROVED' ? 'Approve Review' : 'Reject Review'}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500">
                    {moderateAction === 'APPROVED' 
                      ? 'This review will be visible to all users.' 
                      : 'This review will be hidden from users.'}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <textarea
                  value={moderationNotes}
                  onChange={(e) => setModerationNotes(e.target.value)}
                  placeholder="Add moderation notes (optional)..."
                  className="block w-full px-2.5 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50 min-h-[60px] resize-none"
                  maxLength={500}
                />
              </div>

              <div className="mt-4 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleModerate}
                  disabled={isLoading}
                  className={`w-full inline-flex justify-center rounded-lg border border-transparent shadow-md px-3 py-1.5 text-xs font-medium text-white hover:shadow-lg focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50 transition-all ${
                    moderateAction === 'APPROVED' 
                      ? 'bg-gradient-to-r from-green-600 to-green-700 hover:shadow-green-500/40 focus:ring-green-500' 
                      : 'bg-gradient-to-r from-red-600 to-red-700 hover:shadow-red-500/40 focus:ring-red-500'
                  }`}
                >
                  {isLoading ? 'Processing...' : moderateAction === 'APPROVED' ? 'Approve' : 'Reject'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModerateModal(false);
                    setModerationNotes('');
                    setModerateAction(null);
                  }}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-100 shadow-neomorph px-3 py-1.5 bg-white text-xs font-medium text-gray-700 hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default ReviewTable;